import React from "react";
import { Table, Button, Tag, Dropdown, MenuProps, Empty } from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  MoreOutlined,
  EditOutlined,
  PrinterOutlined,
  PlayCircleOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { User, Mail, Phone, FileText } from "lucide-react";
import dayjs from "dayjs";
import { tablePagination } from "../../../components";
import { PAGE_SIZE } from "../../../Constant";
import { useSectionAccess } from "../../../Services";
import { QuotationAction } from "../../../Types";

interface QuotationTableProps {
  dataSource: any[];
  loading: boolean;
  totalQuotations: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onProceed: (q: any) => void;
  onEdit: (q: any) => void;
  onPrint: (q: any) => void;
  onDownload: (q: any) => void;
  onDelete: (q: any) => void;
}

export const QuotationTable: React.FC<QuotationTableProps> = ({
  dataSource,
  loading,
  totalQuotations,
  currentPage,
  onPageChange,
  onProceed,
  onEdit,
  onPrint,
  onDownload,
  onDelete,
}) => {
  const { isAllowed } = useSectionAccess("quotation");
  const columns = [
    {
      title: "Date Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <CalendarOutlined className="text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Quotation Name / Label",
      dataIndex: "quotation_name",
      key: "quotation_name",
      render: (v: string, rec: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-gray-800 text-xs flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            {v || `Quotation #${rec.id.substring(0, 8)}`}
          </span>
          <span className="text-[10px] text-gray-400 font-mono">Ref: {rec.id}</span>
        </div>
      ),
    },
    {
      title: "Customer Details",
      key: "customer_details",
      render: (_: any, rec: any) => {
        if (rec.customer_id) {
          return (
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-gray-700 text-xs flex items-center gap-1">
                <User className="w-3.5 h-3.5 text-indigo-400" />
                {rec.customer_name || "Registered Customer"}
              </span>
              {rec.customer_phone && (
                <span className="text-[10px] text-gray-500 flex items-center gap-1">
                  <Phone className="w-3 h-3 text-gray-400" />
                  {rec.customer_phone}
                </span>
              )}
            </div>
          );
        }

        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-gray-700 text-xs flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-gray-400" />
              {rec.temp_customer_name || "Walk-in (Temp)"}
              <Tag color="orange" className="text-[8px] font-bold py-0 px-1 border-none leading-normal shrink-0">TEMP</Tag>
            </span>
            {rec.temp_customer_phone && (
              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                <Phone className="w-3 h-3 text-gray-400" />
                {rec.temp_customer_phone}
              </span>
            )}
            {rec.temp_customer_email && (
              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                <Mail className="w-3 h-3 text-gray-400" />
                {rec.temp_customer_email}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Items",
      key: "items_count",
      render: (_: any, rec: any) => (
        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-semibold">
          {rec.cart_data?.length || 0} items
        </span>
      ),
    },
    {
      title: "Quotation Value",
      key: "quotation_value",
      render: (_: any, rec: any) => {
        const total = rec.cart_data?.reduce((sum: number, item: any) => sum + item.price * item.qty, 0) || 0;
        return (
          <span className="font-bold text-indigo-600 text-xs">
            LKR {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        if (status === "completed") {
          return (
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
              <CheckCircleFilled className="text-emerald-500" />
              Completed
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
            Pending
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, rec: any) => {
        const menuItems: MenuProps["items"] = [
          ...(isAllowed(QuotationAction.ConvertToSale)
            ? [
                {
                  key: "proceed",
                  label: "Proceed to Sale (Checkout)",
                  icon: <PlayCircleOutlined className="text-green-500" />,
                  onClick: () => onProceed(rec),
                },
              ]
            : []),
          ...(isAllowed(QuotationAction.Edit)
            ? [
                {
                  key: "edit",
                  label: "Edit / Update Quotation",
                  icon: <EditOutlined className="text-indigo-500" />,
                  onClick: () => onEdit(rec),
                },
              ]
            : []),
          ...(isAllowed(QuotationAction.Print)
            ? [{ key: "divider1", type: "divider" as const }]
            : []),
          ...(isAllowed(QuotationAction.Print)
            ? [
                {
                  key: "print",
                  label: "Print Quotation",
                  icon: <PrinterOutlined />,
                  onClick: () => onPrint(rec),
                },
              ]
            : []),
          ...(isAllowed(QuotationAction.Print)
            ? [
                {
                  key: "download",
                  label: "Download PDF",
                  icon: <FileText className="w-3.5 h-3.5 text-gray-400" />,
                  onClick: () => onDownload(rec),
                },
              ]
            : []),
          ...(isAllowed(QuotationAction.Print) || isAllowed(QuotationAction.Delete)
            ? [{ key: "divider2", type: "divider" as const }]
            : []),
          ...(isAllowed(QuotationAction.Delete)
            ? [
                {
                  key: "delete",
                  label: "Delete Quotation",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onDelete(rec),
                },
              ]
            : []),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} className="text-gray-500 hover:text-gray-700" />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      rowKey="id"
      loading={loading}
      pagination={tablePagination({
        current: currentPage,
        pageSize: PAGE_SIZE,
        total: totalQuotations,
        onChange: onPageChange,
        itemName: "quotations",
      })}
      scroll={{ x: 1000 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No quotations saved yet."
          />
        ),
      }}
    />
  );
};
